import { useState, useEffect } from 'react';
import { Save, RotateCcw, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { adminApi } from '../lib/api';
import type { SystemSetting } from '../lib/api';
import { Card, PageHeader, Button, Badge, Spinner } from '../components/ui';

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  aws_email: 'AWS Email (SES)',
  gohighlevel: 'GoHighLevel',
  planning_center: 'Planning Center',
  backblaze: 'Backblaze B2',
};

const CATEGORY_ORDER = ['general', 'aws_email', 'gohighlevel', 'planning_center', 'backblaze'];

export default function AdminSystemSettings() {
  const [settings, setSettings] = useState<Record<string, SystemSetting[]>>({});
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      if (response.success && response.data) {
        setSettings(response.data.settings);
      } else {
        setError(response.message || 'Failed to load settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (key: string, currentValue: string | null, isSecret: boolean) => {
    setEditingKeys((prev) => new Set(prev).add(key));
    setEditValues((prev) => ({ ...prev, [key]: isSecret ? '' : (currentValue || '') }));
  };

  const cancelEditing = (key: string) => {
    setEditingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setEditValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async (key: string) => {
    const value = editValues[key];
    if (value === undefined || value === '') return;

    setSaving(key);
    setError(null);
    try {
      const response = await adminApi.updateSetting(key, value);
      if (response.success) {
        setSuccess(`${key} updated successfully`);
        cancelEditing(key);
        await loadSettings();
      } else {
        setError(response.message || 'Failed to update setting');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (key: string) => {
    setSaving(key);
    setError(null);
    try {
      const response = await adminApi.deleteSetting(key);
      if (response.success) {
        setSuccess(`${key} reset to default`);
        cancelEditing(key);
        await loadSettings();
      } else {
        setError(response.message || 'Failed to reset setting');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset setting');
    } finally {
      setSaving(null);
    }
  };

  const toggleReveal = (key: string) => {
    setRevealedSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl">
        <PageHeader
          eyebrow="Admin"
          title="System Settings"
          description="Configure system-wide settings. Values set here override environment defaults. Reset to revert to the environment/wrangler value."
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-success-soft border border-line rounded-[11px] flex items-center gap-2 text-success text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {success}
          </div>
        )}

        <div className="space-y-6">
          {CATEGORY_ORDER.map((category) => {
            const items = settings[category];
            if (!items || items.length === 0) return null;

            return (
              <Card key={category} className="overflow-hidden">
                <div className="px-5 py-3 bg-surface-2 border-b border-line">
                  <h2 className="font-display text-sm font-semibold text-ink">
                    {CATEGORY_LABELS[category] || category}
                  </h2>
                </div>
                <div className="divide-y divide-[var(--line-2)]">
                  {items.map((setting) => {
                    const isEditing = editingKeys.has(setting.key);
                    const isSaving = saving === setting.key;

                    return (
                      <div key={setting.key} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono font-medium text-ink">
                                {setting.key}
                              </code>
                              {setting.isSecret && (
                                <Badge tone="warning">Secret</Badge>
                              )}
                              <Badge tone={
                                setting.source === 'db'
                                  ? 'accent'
                                  : setting.source === 'env'
                                    ? 'neutral'
                                    : 'danger'
                              }>
                                {setting.source === 'db' ? 'Custom' : setting.source === 'env' ? 'Default' : 'Not set'}
                              </Badge>
                            </div>
                            <p className="text-xs text-ink-3 mt-1">{setting.description}</p>

                            {isEditing ? (
                              <div className="mt-2 flex items-center gap-2">
                                <input
                                  type={setting.isSecret && !revealedSecrets.has(setting.key) ? 'password' : 'text'}
                                  value={editValues[setting.key] || ''}
                                  onChange={(e) =>
                                    setEditValues((prev) => ({ ...prev, [setting.key]: e.target.value }))
                                  }
                                  placeholder={setting.isSecret ? 'Enter new value...' : 'Enter value...'}
                                  className="ds-input !py-1.5 text-sm font-mono"
                                  disabled={isSaving}
                                />
                                {setting.isSecret && (
                                  <button
                                    onClick={() => toggleReveal(setting.key)}
                                    className="p-1.5 text-ink-3 hover:text-accent"
                                    title={revealedSecrets.has(setting.key) ? 'Hide' : 'Show'}
                                  >
                                    {revealedSecrets.has(setting.key) ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                                <Button
                                  variant="primary"
                                  icon={Save}
                                  onClick={() => handleSave(setting.key)}
                                  disabled={isSaving || !editValues[setting.key]}
                                  className="!h-[34px] text-xs"
                                >
                                  {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  onClick={() => cancelEditing(setting.key)}
                                  disabled={isSaving}
                                  className="!h-[34px] text-xs"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="mt-2 flex items-center gap-2">
                                <code className="text-sm text-ink-2 font-mono">
                                  {setting.value || <span className="text-ink-3 italic">Not configured</span>}
                                </code>
                              </div>
                            )}
                          </div>

                          {!isEditing && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                onClick={() => startEditing(setting.key, setting.value, setting.isSecret)}
                                className="!h-[34px] text-xs"
                              >
                                {setting.isSecret ? 'Change' : 'Edit'}
                              </Button>
                              {setting.source === 'db' && (
                                <Button
                                  variant="ghost"
                                  icon={RotateCcw}
                                  onClick={() => handleReset(setting.key)}
                                  disabled={isSaving}
                                  className="!h-[34px] text-xs"
                                  title="Reset to default (env value)"
                                >
                                  Reset
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
